import { isString, isPlainObject, isFunction, isArray, isEmpty, multiplePathArgs, isNil, deepEqual } from "./lodash.js";
const reEscapeChar = /\\(\\)?/g;
const rePropName = RegExp(
  // Match anything that isn't a dot or bracket.
  `[^.[\\]]+|\\[(?:([^"'][^[]*)|(["'])((?:(?!\\2)[^\\\\]|\\\\.)*?)\\2)\\]|(?=(?:\\.|\\[\\])(?:\\.|\\[\\]|$))`,
  "g"
);
const stringToObjectPath = (string) => {
  const result = [];
  const types = [];
  if (!isString(string) || !string.length)
    return [result, types];
  string.replace(rePropName, (match, expression, quote, subString) => {
    let key = match;
    if (quote)
      key = subString.replace(reEscapeChar, "$1");
    else if (expression)
      key = expression.trim();
    types.push(/\[(?:0|[1-9]\d*)\]/.test(match) ? "array" : "object");
    result.push(key);
    return match;
  });
  return [result, types];
};
const fromEntries = (entries) => {
  try {
    return Object.fromEntries(entries);
  } catch {
    return {};
  }
};
const toEntries = (object) => {
  try {
    return Object.entries(object);
  } catch {
    return [];
  }
};
const copy = (value) => {
  const copyArray = (arr, callback) => {
    const keys = Object.keys(arr);
    const a2 = new Array(keys.length);
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const cur = arr[key];
      if (typeof cur !== "object" || isNil(cur))
        a2[key] = cur;
      else if (cur instanceof Date)
        a2[key] = new Date(cur);
      else
        a2[key] = callback(cur);
    }
    return a2;
  };
  const copyStructure = (structure) => {
    if (typeof structure !== "object" || isNil(structure))
      return structure;
    if (structure instanceof Date)
      return new Date(structure);
    if (Array.isArray(structure))
      return copyArray(structure, copyStructure);
    if (structure instanceof Map)
      return new Map(copyArray(Array.from(structure), copyStructure));
    if (structure instanceof Set)
      return new Set(copyArray(Array.from(structure), copyStructure));
    const structureData = {};
    for (const key in structure) {
      const cur = structure[key];
      if (typeof cur !== "object" || isNil(cur))
        structureData[key] = cur;
      else if (cur instanceof Date)
        structureData[key] = new Date(cur);
      else if (cur instanceof Map)
        structureData[key] = new Map(copyArray(Array.from(cur), copyStructure));
      else if (cur instanceof Set)
        structureData[key] = new Set(copyArray(Array.from(cur), copyStructure));
      else
        structureData[key] = copyStructure(cur);
    }
    return structureData;
  };
  return copyStructure(value);
};
const mapKeys = (object, iteratee) => isPlainObject(object) && isFunction(iteratee) ? fromEntries(toEntries(object).map((el) => [iteratee(el[1], el[0], object), el[1]])) : {};
const mapValues = (object, iteratee) => isPlainObject(object) && isFunction(iteratee) ? fromEntries(toEntries(object).map((el) => [el[0], iteratee(el[1], el[0], object)])) : {};
const setProperty = (structure, path, value) => {
  if (!isPlainObject(structure) && !isArray(structure) || !isString(path))
    return structure;
  const structureCopy = copy(structure);
  let temp = structureCopy;
  const [objectPath, types] = stringToObjectPath(path);
  objectPath.forEach((key, index) => {
    if (!Object.hasOwn(temp, key))
      temp[key] = types[index + 1] === "array" ? [] : {};
    if (index === objectPath.length - 1)
      temp[key] = value;
    else
      temp = temp[key];
  });
  return structureCopy;
};
const getProperty = (object, path) => {
  if (!isPlainObject(object) && !isArray(object) || !isString(path))
    return;
  let objectValue = object;
  stringToObjectPath(path)[0].forEach((key) => {
    objectValue = Object.hasOwn(objectValue ?? {}, key) ? objectValue[key] : void 0;
  });
  return objectValue;
};
const deleteProperty = (structure, path) => {
  if (!isPlainObject(structure) && !isArray(structure) || !isString(path))
    return {};
  const structureCopy = copy(structure);
  let temp = structureCopy;
  const [objectPath] = stringToObjectPath(path);
  objectPath.forEach((key, index) => {
    if (!Object.hasOwn(temp, key))
      return structureCopy;
    if (index === objectPath.length - 1)
      isArray(temp) ? temp.splice(Number(key), 1) : delete temp[key];
    else
      temp = temp[key];
  });
  return structureCopy;
};
const deleteDeepProperty = (structure, path) => {
  if (!isPlainObject(structure) && !isArray(structure) || !isString(path))
    return {};
  const structureCopy = copy(structure);
  let temp = structureCopy;
  const [objectPath] = stringToObjectPath(path);
  const returnObjectPath = objectPath.slice(0, objectPath.length - 1).reduce((acc, _, ind, arr) => {
    acc.push(arr.slice(0, ind + 1));
    return acc;
  }, []).reverse();
  objectPath.forEach((key, index) => {
    if (!Object.hasOwn(temp, key))
      return structureCopy;
    if (index === objectPath.length - 1) {
      isArray(temp) ? temp.splice(Number(key), 1) : delete temp[key];
      returnObjectPath.forEach((parentPath) => {
        temp = structureCopy;
        temp = parentPath.forEach((path2, ind, arr) => {
          if (ind === arr.length - 1 && isEmpty(temp[path2]))
            isArray(temp) ? temp.splice(Number(path2), 1) : delete temp[path2];
          else
            temp = temp[path2];
        });
      });
    } else
      temp = temp[key];
  });
  return structureCopy;
};
const omit = (object, keys) => {
  const params = multiplePathArgs(keys);
  if (isNil(params) || !isPlainObject(object))
    return {};
  let objectCopy = copy(object);
  params.forEach((path) => {
    objectCopy = deleteProperty(objectCopy, path);
  });
  return objectCopy;
};
const pick = (object, keys) => {
  const params = multiplePathArgs(keys);
  if (isNil(params) || !isPlainObject(object))
    return {};
  return params.reduce((acc, path) => {
    const objValue = getProperty(object, path);
    return objValue !== void 0 ? setProperty(acc, path, objValue) : acc;
  }, {});
};
const uniq = (value) => {
  if (!isArray(value))
    return [];
  return value.reduce((acc, el) => {
    if (!acc.some((item) => deepEqual(el, item)))
      acc.push(el);
    return acc;
  }, []);
};
const uniqBy = (value, iterator) => {
  if (!isArray(value) || !(isFunction(iterator) || isString(iterator) && iterator.length))
    return [];
  return value.filter((el, ind, arr) => {
    const matchedIndex = value.findIndex(
      (item) => isFunction(iterator) ? iterator(item, ind, arr) : deepEqual(getProperty(item, iterator), getProperty(el, iterator))
    );
    return matchedIndex === -1 || matchedIndex === ind;
  });
};
export {
  copy,
  deleteDeepProperty,
  deleteProperty,
  fromEntries,
  getProperty,
  mapKeys,
  mapValues,
  omit,
  pick,
  setProperty,
  stringToObjectPath,
  toEntries,
  uniq,
  uniqBy
};
//# sourceMappingURL=structures.js.map
